import { useEffect, useState } from 'react';
import { API_ORIGIN, api } from '../api/client';

// Friendly text for each error reason the API redirects with. Keys match the
// `?error=…` reasons from `/auth/google/callback` (apps/api/src/routes/auth.ts).
// New reasons added there should add a line here so the user sees a real
// message rather than the raw code.
const ERROR_MESSAGES: Record<string, string> = {
  invalid_state:
    "Sign-in took too long or the link expired. Please try again.",
  email_not_verified:
    "Your Google account email isn't verified. Verify it with Google and try again.",
  db_unavailable:
    "Our database briefly hiccuped. Please try again in a moment — your account is fine.",
  server_error:
    "Something went wrong on our side. Please try again, or contact support if it persists.",
};

export default function LoginPage() {
  // Probe whether Google OAuth is wired up server-side. While we're waiting,
  // render the button (optimistic — most of the time it IS configured); if
  // the probe says no, swap to a clear "not configured" panel so users don't
  // get an opaque 503 JSON page from the API.
  const [oauthEnabled, setOauthEnabled] = useState<boolean | null>(null);
  useEffect(() => {
    api
      .get<{ googleOAuthEnabled: boolean }>('/auth/status')
      .then((r) => setOauthEnabled(r.googleOAuthEnabled))
      .catch(() => setOauthEnabled(false));
  }, []);

  // OAuth callback errors are surfaced via `?error=<reason>` — the API
  // redirects here with a non-sensitive reason code rather than rendering
  // raw `{"error":"internal_error"}` JSON in the browser bar.
  const [errorReason] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    return params.get('error');
  });
  const errorMessage = errorReason
    ? ERROR_MESSAGES[errorReason] ?? ERROR_MESSAGES.server_error
    : null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FB] p-6">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-xl border border-slate-100 p-10">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-[#6344C6] text-white flex items-center justify-center font-bold">P</div>
          <div>
            <h1 className="text-xl font-bold">PeopleAwesomeness</h1>
            <p className="text-xs text-slate-500">HR, without the fuss</p>
          </div>
        </div>
        <h2 className="text-2xl font-bold mb-2">Sign in</h2>
        <p className="text-sm text-slate-500 mb-8">
          We only support Google sign-in. By continuing you agree to our Terms and Privacy Policy.
          We store the minimum necessary to operate your workspace (email, name, avatar).
        </p>
        {errorMessage && (
          <div
            role="alert"
            className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 mb-4"
          >
            <p className="font-semibold mb-1">Sign-in didn&apos;t complete.</p>
            <p className="text-xs leading-relaxed">{errorMessage}</p>
          </div>
        )}
        {oauthEnabled === false ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <p className="font-semibold mb-1">Sign-in is not configured yet.</p>
            <p className="text-xs leading-relaxed">
              The site administrator hasn&apos;t finished setting up Google
              OAuth. See the{' '}
              <a
                className="underline"
                href="https://github.com/carisatalbot/peopleAwesomeness/blob/main/HiSarah_design_instructions/OAUTH_SETUP.md"
                target="_blank"
                rel="noreferrer"
              >
                OAuth setup runbook
              </a>{' '}
              for the steps.
            </p>
          </div>
        ) : (
          <a
            href={`${API_ORIGIN}/auth/google/start`}
            aria-disabled={oauthEnabled === null}
            className={`w-full inline-flex items-center justify-center gap-3 text-white font-medium rounded-xl py-3 transition-colors ${
              oauthEnabled === null
                ? 'bg-[#6344C6]/60 cursor-wait'
                : 'bg-[#6344C6] hover:bg-[#5a3eb8]'
            }`}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true"><path fill="#fff" d="M21.35 11.1h-9.17v2.92h5.3a4.52 4.52 0 0 1-1.96 2.96v2.45h3.17c1.86-1.71 2.94-4.24 2.94-7.33a11 11 0 0 0-.28-2.92z"/><path fill="#fff" opacity=".9" d="M12.18 21c2.66 0 4.89-.87 6.52-2.37l-3.17-2.45c-.88.6-2.01.95-3.35.95-2.58 0-4.76-1.74-5.54-4.08H3.38v2.56A9.82 9.82 0 0 0 12.18 21z"/><path fill="#fff" opacity=".7" d="M6.64 13.05a5.89 5.89 0 0 1 0-3.75V6.74H3.38a9.82 9.82 0 0 0 0 8.87l3.26-2.56z"/><path fill="#fff" opacity=".5" d="M12.18 5.22c1.45 0 2.75.5 3.78 1.48l2.81-2.81A9.82 9.82 0 0 0 3.38 6.74l3.26 2.56c.78-2.34 2.96-4.08 5.54-4.08z"/></svg>
            Continue with Google
          </a>
        )}
        <p className="text-xs text-slate-400 mt-6 leading-relaxed">
          GDPR: Your data lives in the EU. You can export or delete your account at any time from Settings.
        </p>
      </div>
    </div>
  );
}
